import React, { useEffect, useMemo, useRef, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@material-ui/core';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  postProjectTypes,
  putProjectTypes,
  deleteProjectType,
} from 'store/projectTypes/projectTypesOperations';
import ProjectTypesActions from 'store/projectTypes/projectTypesActions';
import { InlineCreateSelector } from 'lib';
import styles from './styles';

const ProjectTypes = ({
  classes,
  setFieldValue,
  project,
  errors,
  touched,
  value,
  handleBlur,
  openSnackbar,
  isSubmitting,
}) => {
  const dispatch = useDispatch();
  const projectTypesOptions = useSelector(
    (state) => state.projectTypes.projectTypes.projectTypes,
  );
  const snackBarInfo = useSelector(
    (state) => state.projectTypes.projectTypes.snackBarInfo,
  );

  const [ifProjectExists, setIfProjectExists] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deletedTypeName, setDeletedTypeName] = useState(null);
  const pendingDeleteRef = useRef(null);

  useEffect(() => {
    if (!snackBarInfo) return;
    openSnackbar?.(snackBarInfo.type, snackBarInfo.text);
    dispatch(ProjectTypesActions.snackBarInfo(null));
  }, [snackBarInfo, dispatch, openSnackbar]);

  useEffect(() => {
    if (!project || !value || isSubmitting) return;
    const exists = projectTypesOptions.find((elem) => elem.name === project.type);
    if (!exists && project.type) {
      setDeletedTypeName(project.type);
    } else {
      setDeletedTypeName(null);
    }
  }, [project, value, projectTypesOptions, isSubmitting]);

  const selectedId = useMemo(() => {
    if (!value) return null;
    return projectTypesOptions.find((opt) => opt.name === value)?._id || null;
  }, [projectTypesOptions, value]);

  const handleChange = (id) => {
    if (errors?.type) delete errors.type;
    if (!id) {
      setFieldValue('type', '');
      return;
    }
    const next = projectTypesOptions.find((opt) => opt._id === id);
    setFieldValue('type', next?.name || '');
    setDeletedTypeName(null);
  };

  const handleCreate = async (name) => {
    const created = await dispatch(postProjectTypes({ name }));
    if (created?._id) {
      setFieldValue('type', created.name);
      setDeletedTypeName(null);
    }
    return created;
  };

  const handleRename = async (id, name) => {
    const updated = await dispatch(putProjectTypes(id, { name }));
    if (updated?._id && selectedId === id) {
      setFieldValue('type', updated.name || name);
    }
    return updated;
  };

  const finalizeDelete = async (id) => {
    await dispatch(deleteProjectType(id));
    if (selectedId === id) setFieldValue('type', '');
  };

  const handleDelete = (id) =>
    new Promise((resolve, reject) => {
      const item = projectTypesOptions.find((elem) => elem._id === id);
      if (item?.hasSubmittedWLs) {
        setIfProjectExists(true);
        setItemToDelete(id);
        pendingDeleteRef.current = { resolve, reject };
        return;
      }
      finalizeDelete(id).then(resolve).catch(reject);
    });

  const confirmDelete = async () => {
    const id = itemToDelete;
    setIfProjectExists(false);
    setItemToDelete(null);
    const pending = pendingDeleteRef.current;
    pendingDeleteRef.current = null;
    try {
      await finalizeDelete(id);
      pending?.resolve?.();
    } catch (err) {
      pending?.reject?.(err);
    }
  };

  const cancelDelete = () => {
    setIfProjectExists(false);
    setItemToDelete(null);
    const pending = pendingDeleteRef.current;
    pendingDeleteRef.current = null;
    pending?.reject?.(new Error('delete-cancelled'));
  };

  return (
    <>
      <InlineCreateSelector
        name="type"
        id="type"
        value={selectedId}
        options={projectTypesOptions}
        onChange={handleChange}
        onCreate={handleCreate}
        onRename={handleRename}
        onDelete={handleDelete}
        addLabel="+ Add Project Type"
        error={Boolean(errors?.type) && Boolean(touched?.type)}
        onBlur={handleBlur}
      />
      {deletedTypeName && (
        <Typography variant="body1" color="textSecondary">
          This project type was deleted. Do you want to add it again?
        </Typography>
      )}
      {ifProjectExists && (
        <Dialog
          open={Boolean(ifProjectExists)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            classes: {
              root: classes.dialogRoot,
            },
          }}
          disableEscapeKeyDown
        >
          <DialogContent>
            <Typography variant="h5" component="div">
              There are submitted Work-Logs in the system with this project type. Are you sure
              that you want to delete it?
            </Typography>
          </DialogContent>
          <DialogActions>
            <div className={classes.flexBox}>
              <Button
                color="primary"
                variant="outlined"
                size="medium"
                className={classes.saveButton}
                disableTouchRipple
                onClick={confirmDelete}
              >
                Yes
              </Button>
              <Button
                color="secondary"
                variant="outlined"
                size="medium"
                className={classnames(classes.cancelButton)}
                onClick={cancelDelete}
                disableTouchRipple
              >
                No
              </Button>
            </div>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default withStyles(styles)(ProjectTypes);
